import { cn } from '../lib/utils';

/**
 * Show and hide columns, per person.
 *
 * Andrea, 29 Jul: "Can you enable columns to be hidden and unhidden when
 * needed". Saved against her account rather than the browser, so hiding a
 * column on her screen does not change anyone else's and does not come back
 * when she signs in somewhere else.
 *
 * The Job column is not offered. It carries the community, the lot and the
 * click target that opens the job, so hiding it leaves rows nobody can
 * identify or edit.
 */
export function ColumnsPanel({
    x,
    y,
    columns,
    hidden,
    lockedIds,
    onChange,
    onClose,
}: {
    x: number;
    y: number;
    columns: { id: string; label: string }[];
    hidden: string[];
    lockedIds: string[];
    onChange: (hidden: string[]) => void;
    onClose: () => void;
}) {
    const hiddenSet = new Set(hidden);
    const shownCount = columns.filter((c) => !hiddenSet.has(c.id)).length;

    function toggle(id: string) {
        if (lockedIds.includes(id)) return;
        const next = new Set(hiddenSet);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        onChange([...next]);
    }

    return (
        <>
            <div className="fixed inset-0 z-40" onClick={onClose} />
            <div
                role="dialog"
                aria-label="Show and hide columns"
                style={{ position: 'fixed', left: x, top: y, width: 264 }}
                className="z-50 bg-white dark:bg-graphite-900 border border-graphite-300 dark:border-graphite-700 rounded shadow-card-md overflow-hidden"
            >
                <div className="px-3 py-2 border-b border-graphite-200 dark:border-graphite-800 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-600 dark:text-graphite-400">
                        Columns
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="text-graphite-500 hover:text-graphite-900 dark:hover:text-graphite-200 -m-1 p-1"
                    >
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                    </button>
                </div>

                <div className="max-h-72 overflow-auto px-2 py-2 flex flex-col gap-0.5">
                    {columns.map((c) => {
                        const locked = lockedIds.includes(c.id);
                        const shown = !hiddenSet.has(c.id);
                        return (
                            <label
                                key={c.id}
                                title={locked ? 'This column cannot be hidden' : undefined}
                                className={cn(
                                    'flex items-center gap-2 px-1.5 py-1 rounded select-none',
                                    locked
                                        ? 'opacity-50 cursor-default'
                                        : 'cursor-pointer hover:bg-graphite-100 dark:hover:bg-graphite-800',
                                )}
                            >
                                <input
                                    type="checkbox"
                                    checked={shown}
                                    disabled={locked}
                                    onChange={() => toggle(c.id)}
                                    className="accent-signal-500"
                                />
                                <span className="text-xs text-graphite-800 dark:text-graphite-200 truncate">
                                    {c.label}
                                </span>
                            </label>
                        );
                    })}
                </div>

                <div className="px-2 py-2 border-t border-graphite-200 dark:border-graphite-800 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onChange([])}
                        disabled={hidden.length === 0}
                        className="px-2 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider text-graphite-600 dark:text-graphite-400 hover:bg-graphite-100 dark:hover:bg-graphite-800 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
                    >
                        Show all
                    </button>
                    <div className="flex-1" />
                    <span className="font-mono text-[10px] text-graphite-400 dark:text-graphite-600">
                        {shownCount} of {columns.length}
                    </span>
                </div>
            </div>
        </>
    );
}
